import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    FaTimes, FaPhone, FaCheckCircle, FaMapMarkerAlt,
    FaClock, FaUser, FaEnvelope, FaCreditCard,
    FaShieldAlt, FaReceipt,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import AppContext from '../../../../context/AppContext';
import '../../../../assets/css/customer/publicCustomerCss/CusPackages/cuspackages.css';

const BookingForm = ({ packageId, packageData, onClose }) => {
    const navigate = useNavigate();
    const { validateForm, baseUrl, authToken,imageBaseUrl } = useContext(AppContext);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({ phone_no: '' });
    const [errors, setErrors] = useState({});
    const [commissionRate, setCommissionRate] = useState(null);

     useEffect(() => {
        axios
            .get(`${baseUrl}/customer/commission-rate`, {
                headers: { Authorization: `Bearer ${authToken}` },
            })
            .then((res) => setCommissionRate(res.data?.commission_rate ?? 0))
            .catch(() => setCommissionRate(0));
    }, []);

    if (!packageData) return null;

    const title    = packageData.package_name;
    const image    = packageData.package_image
        ? `${imageBaseUrl}/${packageData.package_image}`
        : null;
    const price    = parseFloat(packageData.package_price) || 0;
    const days     = packageData.duration_days;
    const nights   = packageData.duration_nights;
    const loc      = packageData.location || packageData.package_destination;
    const cat      = packageData.package_category;

    const customerId    = localStorage.getItem('customerId');
    const customerName  = localStorage.getItem('userName')  || '';
    const customerEmail = localStorage.getItem('customerEmail') || '';

    const rate      = commissionRate ?? 0;
    const reserved  = +(price * (rate / 100)).toFixed(2);
    const remaining = +(price - reserved).toFixed(2);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const { isValid, errors: errs } = validateForm([
            { field: 'phone_no', type: 'phone', value: formData.phone_no },
        ]);
        if (!isValid) { setErrors(errs); return; }

        setIsSubmitting(true);

        axios
            .post(
                `${baseUrl}/customer/bookings`,
                {
                    jjpackage_id:  packageId,
                    phone_no:      formData.phone_no,
                    jjcustomer_id: customerId,
                },
                { headers: { Authorization: `Bearer ${authToken}` } }
            )
            .then((bookingRes) => {
                const booking = bookingRes.data?.booking || bookingRes.data;
                const bookingId = booking?.id || booking?.jjbooking_id;

                if (!bookingId) {
                    throw new Error(bookingRes.data?.message || 'Booking creation failed.');
                }

    
                return axios.post(
                    `${baseUrl}/customer/payment/checkout`,
                    {jjbooking_id: bookingId },
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );
            })
            .then((paymentRes) => {
                const stripeUrl = paymentRes.data?.url;
                if (!stripeUrl) throw new Error('Payment session could not be created.');
               
                window.location.href = stripeUrl;
            })
            .catch((err) => {
                console.error('Booking/Payment error:', err);
                Swal.fire({
                    title: 'Error',
                    text: err.message || 'Unable to process. Please try again.',
                    icon: 'error',
                    confirmButtonColor: '#2C5F7C',
                });
            })
            .finally(() => setIsSubmitting(false));
    };

    return (
        <div className="bf-wrap">

            {/* Header */}
            <div className="bf-head">
                <div className="bf-head-left">
                    <FaCreditCard className="bf-head-icon" />
                    <div>
                        <h2 className="bf-head-title">Confirm & Pay</h2>
                        <p className="bf-head-sub">Secure your spot — pay only 10% now</p>
                    </div>
                </div>
                <button className="bf-close" onClick={onClose} aria-label="Close">
                    <FaTimes />
                </button>
            </div>

            <div className="bf-summary">
                {image && <img src={image} alt={title} className="bf-sum-img" />}
                <div className="bf-sum-body">
                    {cat && <span className="bf-sum-badge">{cat}</span>}
                    <h3 className="bf-sum-title">{title}</h3>
                    <div className="bf-sum-meta">
                        {loc && (
                            <span>
                                <FaMapMarkerAlt style={{ color: '#FFB800', marginRight: 4 }} />
                                {loc}
                            </span>
                        )}
                        {(days || nights) && (
                            <span>
                                <FaClock style={{ color: '#FFB800', marginRight: 4 }} />
                                {days && `${days}D`}{days && nights && ' / '}{nights && `${nights}N`}
                            </span>
                        )}
                    </div>
                </div>
                {price > 0 && (
                    <div className="bf-sum-price">
                        <span className="bf-sum-pkr">PKR</span>
                        <span className="bf-sum-amount">{price.toLocaleString()}</span>
                    </div>
                )}
            </div>

            {/* Form */}
            <form className="bf-form" onSubmit={handleSubmit} noValidate>

                {/* Contact Details */}
                <div className="bf-section">
                    <h3 className="bf-sec-title">
                        <FaPhone className="bf-sec-icon" /> Contact Details
                    </h3>

                    <div className="bf-field">
                        <label className="bf-label">
                            <FaUser className="bf-lbl-icon" /> Full Name
                        </label>
                        <input
                            type="text"
                            className="bf-input bf-input--readonly"
                            value={customerName}
                            readOnly
                        />
                    </div>

                    <div className="bf-field">
                        <label className="bf-label">
                            <FaEnvelope className="bf-lbl-icon" /> Email Address
                        </label>
                        <input
                            type="email"
                            className="bf-input bf-input--readonly"
                            value={customerEmail}
                            readOnly
                        />
                    </div>

]                    <div className="bf-field">
                        <label className="bf-label">
                            <FaPhone className="bf-lbl-icon" />
                            Phone Number <span className="bf-req">*</span>
                        </label>
                        <input
                            type="tel"
                            name="phone_no"
                            className={`bf-input${errors.phone_no ? ' bf-err-input' : ''}`}
                            value={formData.phone_no}
                            onChange={handleChange}
                            placeholder="03XX-XXXXXXX"
                            autoComplete="tel"
                        />
                        {errors.phone_no && (
                            <span className="bf-err-msg">⚠ {errors.phone_no}</span>
                        )}
                    </div>
                </div>

                <div className="bf-section">
                    <h3 className="bf-sec-title">
                        <FaReceipt className="bf-sec-icon" /> Payment Breakdown
                    </h3>
                    <div className="bf-breakdown">
                        <div className="bf-breakdown-row">
                            <span className="bf-bd-label">Total Package Cost</span>
                            <span className="bf-bd-value">PKR {price.toLocaleString()}</span>
                        </div>
                        <div className="bf-breakdown-row bf-breakdown-row--highlight">
                            <div className="bf-bd-label-group">
                                <span className="bf-bd-label">Reservation Amount</span>
                                <span className="bf-bd-badge">Pay Now · {rate}%</span>
                            </div>
                            <span className="bf-bd-value bf-bd-value--accent">
                                PKR {reserved.toLocaleString()}
                            </span>
                        </div>
                        <div className="bf-breakdown-row">
                            <div className="bf-bd-label-group">
                                <span className="bf-bd-label">Remaining Balance</span>
                                <span className="bf-bd-badge bf-bd-badge--muted">Pay Later · {(100 - rate)}%</span>
                            </div>
                            <span className="bf-bd-value bf-bd-value--muted">
                                PKR {remaining.toLocaleString()}
                            </span>
                        </div>
                        <div className="bf-breakdown-note">
                            <FaShieldAlt className="bf-note-icon" />
                            <span>
                                You are only charged <strong>PKR {reserved.toLocaleString()}</strong> today.
                                The remaining balance is due before departure.
                            </span>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="bf-foot">
                    <div className="bf-foot-total">
                        <span className="bf-foot-lbl">Due Today</span>
                        <span className="bf-foot-amt">PKR {reserved.toLocaleString()}</span>
                        <span className="bf-foot-hint">Secure payment via Stripe</span>
                    </div>
                    <button type="submit" className="bf-submit" disabled={isSubmitting}>
                        {isSubmitting
                            ? <><span className="bf-spinner" /> Processing…</>
                            : <><FaCreditCard /> Confirm & Pay</>
                        }
                    </button>
                </div>
            </form>
        </div>
    );
};

export default BookingForm;